import cn from 'classnames'
import { modal_transitionSpeed } from 'ext-ui/style/transitionSpeeds'
import React, { ReactElement } from 'react'
import Modal, { Props as ModalProps } from 'react-modal'
import styled from 'styled-components'

import CloseButton from '../CloseButton/CloseButton'
import style from './BaseModal.style'

export type BaseModalProps = Omit<ModalProps, 'onRequestClose'> & {
  className?: string
  /** If `true`, this Modal is open */
  isOpen: boolean
  /**
   * Use the `popup` (default) variant for simple tasks like showing the status of downloads
   * or to confirm something. This variant contains styling to help
   *
   * Use the `interactive` variant for forms and larger multi-step processes
   */
  variant?: 'popup' | 'interactive'
  /** Function to run on close button click */
  onClose: () => void
  /** Shows the close button if `true` */
  showCloseButton?: boolean
}

function BaseModal(props: BaseModalProps): ReactElement {
  const {
    className = '',
    variant = 'popup',
    isOpen,
    children,
    onClose,
    showCloseButton = true,
    // ...rest
  } = props

  return (
    <Modal
      className={cn('BaseModal-Content', variant)}
      overlayClassName={{
        base: 'BaseModal-Overlay',
        afterOpen: 'BaseModal-Overlay-open',
        beforeClose: 'BaseModal-Overlay-closed',
      }}
      portalClassName={cn('BaseModal', className)}
      isOpen={isOpen}
      onRequestClose={onClose}
      closeTimeoutMS={parseInt(modal_transitionSpeed)}
      bodyOpenClassName="modal-open"
    >
      {showCloseButton && (
        <CloseButton
          className="BaseModal-CloseButton"
          variant="light-on-dark"
          onClick={onClose}
        />
      )}
      <div className="BaseModal-Body">{children}</div>
    </Modal>
  )
}

// We have to use `styled` instead of the `css` prop because the `css` prop
// will automatically put the styled-components mangled className in the
// `className` prop on `<Modal>`. However, that puts the class on the Content,
// preventing us from styling the Overlay. By using `styled`, we are able to
// pass the className to `portalClassName`, which will style the parent of both
// Overlay and Content.
export default styled(BaseModal)`
  ${style}
`
